import MapIcon from '@mui/icons-material/Map';
import { Card, Stack, Typography } from '@mui/material';
import Alert from '/@/components/bayer/alert';
import useUnitProvider from '/@/core/context/unit';
import type { ZonalYield } from '/@/hooks/crop/use-zonal-yield';
import { _ } from '/@/locale';

/**
 * "Promedio histórico zonal" panel: what the area around the field has historically yielded,
 * as a reference for the environment bar next to it.
 *
 * The figure comes from the nearest point of Bayer's ~30km lattice rather than from the field's
 * own department, so the card states the distance to that point instead of naming a department
 * the reading does not really belong to.
 *
 * Sits beside `LastCampaignCard` and shares its shape deliberately — same info background,
 * same radius, same overline/figure/details rhythm — since the two answer the neighbouring
 * questions "what did *this lot* do last year" and "what does *this zone* usually do".
 *
 * `compact` is the phone layout, matching the sibling card: the figure leads and the details
 * stack under it, rather than sitting in a fixed-width column.
 */
function ZonalYieldCard({
    zonalYield,
    crop = 'corn',
    compact,
}: {
    zonalYield: ZonalYield;
    crop?: 'corn' | 'soy';
    compact?: boolean;
}) {
    const units = useUnitProvider();
    // Not `toCornYield`: the step is typed for soy too, and `toYield` picks both
    // the conversion ratio and the unit label per crop.
    const zoneYield = units.toYield(zonalYield.value, crop, 0);

    return (
        <Card
            elevation={0}
            sx={{
                background: (theme) => theme.palette.primary.infoBackground,
                borderRadius: '8px',
                p: 2,
                width: compact ? '100%' : undefined,
            }}
        >
            <Stack spacing={1} width={compact ? undefined : 265}>
                <Typography variant='overline' color='primary.mui.inactive' textTransform='uppercase'>
                    {_('Promedio histórico zonal')}
                </Typography>

                {/* The design named the field's department here, but the figure comes from
                    the nearest lattice point, which is often in the neighbouring one. The
                    distance is the truthful version of the same line, and it is what the AC
                    means by stating the granularity of the data. */}
                <Stack direction='row' spacing={1} alignItems='center'>
                    <MapIcon color='primary' />
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('A {{distance}} km del lote', { distance: zonalYield.distanceKm })}
                    </Typography>
                </Stack>

                <Typography variant='h4' color='primary'>
                    {zoneYield.localeValue}{' '}
                    <Typography variant='h6' component='span' color='primary'>
                        {zoneYield.unit}
                    </Typography>
                </Typography>

                <Typography variant='body2' color='primary'>
                    {_('Fecha de siembra: {{window}}', { window: _(zonalYield.zone.shortName) })}
                </Typography>

                <Alert severity='surface'>{_('Fuente: ensayos de Bayer')}</Alert>
            </Stack>
        </Card>
    );
}

export default ZonalYieldCard;
